/**
 * Ajustes salvos no servidor no modo ao vivo: aplicados por cima dos padrões na abertura e gravados no
 * servidor a cada mudança. Daqui saem os ajustes do boot e o imposto da Meta que as contas de mídia
 * usam (`ctx.settings()` e `ctx.metaTaxPct()`).
 *
 * Tudo fica numa chave só do servidor (kv `painel_ajustes`, `GET/POST /api/painel/ajustes`):
 * - custos, metas, limiares, avisos e backend: as seções do `Settings`, como sempre;
 * - Economia e VSL (`updateEconomics`): em `economicsV2`, só o que o dono mudou (nunca o padrão
 *   inteiro: o `realVsl.ts` lê dali se os blocos da copy e o pitch foram salvos);
 * - o que outros módulos guardam (o Plano, por exemplo): em `painelExtras`, por chave
 *   (`savedExtras(ctx)`).
 * A divisão da coprodução e o líquido por venda (Economia) vêm dos pedidos pagos (`measured` na leitura
 * dos ajustes), não do estudo; o que o dono salvar fica por cima.
 * Contas e pixel da Meta nunca vêm do salvo nem da simulação: as contas são as da Meta real (do boot) e
 * o pixel é o que o servidor informa (`pixelId` na leitura dos ajustes; vazio enquanto não informar).
 * O controle da simulação (`sim:control`) sai do papel no modo ao vivo: cenário, velocidade e hora
 * mudariam números que ainda vêm da simulação.
 *
 * Teto diário de investimento: só o que o dono salvou. Sem teto salvo, 0 = "sem teto" (o padrão da
 * simulação, R$ 400, nunca foi definido pelo dono). O 0 não vai para a simulação (lá ele travaria as
 * ações dela); fica só nos ajustes do modo ao vivo.
 */
import type { AdAccount, DataProvider, EconomicsV2, Settings, Viewer } from '@rota/contracts';
import { getWithRetry } from './http';
import type { RealCtx, RealModule } from './realCore';

/** Imposto da Meta sobre a mídia na conta em real (Ajustes › Custos): 12,15%. */
export const META_TAX_DEFAULT = 0.1215;

const URL_AJUSTES = '/api/painel/ajustes';

/** O que fica guardado no servidor (kv `painel_ajustes`). */
export type StoredSettings = Partial<Omit<Settings, 'economicsV2'>> & {
  /** Só o que o dono mudou em Economia e VSL. */
  economicsV2?: Partial<EconomicsV2>;
  /** Registros de outros módulos (Plano…), por chave. */
  painelExtras?: Record<string, unknown>;
};

/** Ajustes em uso (os do boot, depois os salvos), o imposto da Meta e o que o dono salvou à parte. */
export function createSettingsState() {
  let settings: Settings | null = null;
  let metaTaxPct = META_TAX_DEFAULT;
  let economics: Partial<EconomicsV2> = {};
  let extras: Record<string, unknown> = {};
  let realMeta: Settings['meta'] | null = null;
  /** Teto diário salvo pelo dono (0 = sem teto). */
  let ownerCap = 0;
  /** O salvo foi lido do servidor (sem isso, gravar apagaria o que não foi lido). */
  let loaded = false;
  const apply = (s: Settings | null | undefined) => {
    if (!s) return;
    settings = s;
    const tax = (s as { costs?: { metaTaxPct?: number } }).costs?.metaTaxPct;
    if (typeof tax === 'number' && tax >= 0 && tax < 1) metaTaxPct = tax;
  };
  return {
    settings: () => settings,
    metaTaxPct: () => metaTaxPct,
    apply,
    economics: () => economics,
    setEconomics: (e: Partial<EconomicsV2>) => {
      economics = e;
    },
    extras: () => extras,
    setExtras: (x: Record<string, unknown>) => {
      extras = x;
    },
    realMeta: () => realMeta,
    setRealMeta: (m: Settings['meta']) => {
      realMeta = m;
    },
    ownerCap: () => ownerCap,
    setOwnerCap: (v: number) => {
      ownerCap = v;
    },
    loaded: () => loaded,
    setLoaded: (v: boolean) => {
      loaded = v;
    },
  };
}
export type SettingsState = ReturnType<typeof createSettingsState>;

/**
 * Ajustes salvos pelo dono, com o que ele nunca cadastrou zerado: despesas fixas (`[]`) e imposto
 * sobre a receita (0). Os padrões da simulação para isso são estimativas do estudo, não do negócio.
 */
export function ownerOnly(saved: Partial<Settings> | null): Partial<Settings> {
  const costs = (saved?.costs ?? {}) as Partial<Settings['costs']>;
  return {
    ...(saved ?? {}),
    costs: {
      ...costs,
      expenses: costs.expenses ?? [],
      revenueTaxPct: typeof costs.revenueTaxPct === 'number' ? costs.revenueTaxPct : 0,
    } as Settings['costs'],
  };
}

/** Teto diário que o dono salvou (sem teto salvo, 0). */
export const savedCapOf = (saved: Partial<Settings> | null | undefined): number => {
  const v = (saved?.goals as Partial<Settings['goals']> | undefined)?.dailySpendCap;
  return typeof v === 'number' && v > 0 ? v : 0;
};

/**
 * O que vai para a simulação: sem teto 0 (lá ele bloquearia as ações dela). A simulação segue com o
 * teto dela só para as contas internas; a tela usa o do dono.
 */
export function forSim<P extends Partial<Settings>>(patch: P): P {
  const goals = patch.goals as Partial<Settings['goals']> | undefined;
  if (!goals || !('dailySpendCap' in goals) || (goals.dailySpendCap ?? 0) > 0) return patch;
  const { dailySpendCap: _cap, ...rest } = goals;
  return { ...patch, goals: rest };
}

/** Mescla seção a seção (como o `updateSettings` da simulação), para quando ele não pode gravar. */
function mergeSections(base: Settings, patch: Partial<Settings>): Settings {
  const out = { ...base } as Record<string, unknown>;
  for (const [k, v] of Object.entries(patch)) {
    if (v == null) continue;
    const cur = out[k];
    out[k] =
      Array.isArray(v) || typeof v !== 'object' || cur == null || typeof cur !== 'object'
        ? v
        : { ...(cur as object), ...(v as object) };
  }
  return out as unknown as Settings;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  v != null && typeof v === 'object' && !Array.isArray(v);

/**
 * Separa o que veio do servidor: as seções do `Settings` (sem as contas da Meta, que nunca vêm do
 * salvo), a Economia salva e os registros dos outros módulos.
 */
export function splitStored(saved: StoredSettings | null | undefined): {
  settings: Partial<Settings>;
  economics: Partial<EconomicsV2>;
  extras: Record<string, unknown>;
} {
  const { economicsV2, painelExtras, meta: _meta, ...rest } = (saved ?? {}) as StoredSettings;
  return {
    settings: rest as Partial<Settings>,
    economics: isObj(economicsV2) ? (economicsV2 as Partial<EconomicsV2>) : {},
    extras: isObj(painelExtras) ? painelExtras : {},
  };
}

/** O que vai para o servidor: as seções em uso, a Economia do dono e os registros à parte. */
export function toStored(
  settings: Settings,
  economics: Partial<EconomicsV2>,
  extras: Record<string, unknown>,
): StoredSettings {
  // Contas da Meta e a economia padrão ficam fora: as contas vêm da Meta real e o padrão não é do dono.
  const { meta: _meta, economicsV2: _economics, ...rest } = settings;
  return {
    ...rest,
    ...(Object.keys(economics).length ? { economicsV2: economics } : {}),
    ...(Object.keys(extras).length ? { painelExtras: extras } : {}),
  };
}

/** Contas reais da Meta (as do boot, já trocadas pelas da Meta) e o pixel que o servidor informa. */
export const realMetaOf = (
  accounts: readonly Pick<AdAccount, 'id'>[] | undefined,
  pixelId?: string | null,
): Settings['meta'] => ({
  accountIds: (accounts ?? []).map((a) => a.id),
  pixelId: typeof pixelId === 'string' ? pixelId.trim() : '',
});

/** Papel sem o controle da simulação (modo ao vivo). */
export function withoutSimControl<V extends Pick<Viewer, 'permissions'>>(viewer: V): V {
  return viewer.permissions.includes('sim:control')
    ? { ...viewer, permissions: viewer.permissions.filter((p) => p !== 'sim:control') }
    : viewer;
}

/** Registros guardados à parte por outros módulos (Plano…), no mesmo salvo dos Ajustes. */
export interface SavedExtras {
  get<T>(key: string): T | undefined;
  /** Grava no servidor; lança erro se não deu (a tela não confirma "Salvo" à toa). */
  save(key: string, value: unknown): Promise<void>;
}

const extrasByCtx = new WeakMap<RealCtx, SavedExtras>();

/** Os registros à parte do modo ao vivo deste contexto (null antes do módulo de Ajustes existir). */
export function savedExtras(ctx: RealCtx): SavedExtras | null {
  return extrasByCtx.get(ctx) ?? null;
}

/** Só os números válidos do que o servidor mediu (parte entre 0 e 1; líquidos em centavos positivos). */
export function measuredOf(
  x: Record<string, unknown> | null | undefined,
): Partial<Pick<EconomicsV2, 'ourSharePct' | 'netFront' | 'netUpsell'>> {
  if (!x || typeof x !== 'object') return {};
  const out: Partial<Pick<EconomicsV2, 'ourSharePct' | 'netFront' | 'netUpsell'>> = {};
  const share = x.ourSharePct;
  if (typeof share === 'number' && share > 0 && share < 1) out.ourSharePct = share;
  for (const k of ['netFront', 'netUpsell'] as const) {
    const v = x[k];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) out[k] = Math.round(v);
  }
  return out;
}

export function settingsModule(ctx: RealCtx, state: SettingsState): RealModule {
  const { provider, v2 } = ctx;

  /** Pixel que o servidor informou na leitura (null = não informou). */
  let pixelId: string | null = null;
  /**
   * Divisão da coprodução e líquido por venda medidos nos pedidos pagos (o servidor manda na leitura dos
   * ajustes; só dono e sócio). Entram no lugar dos números do estudo (40,44%, R$ 108,31, R$ 255,53),
   * por baixo do que o dono salvou.
   */
  let measured: Partial<Pick<EconomicsV2, 'ourSharePct' | 'netFront' | 'netUpsell'>> = {};

  /** Lê o salvo do servidor (null = não deu para ler; `{}` = nada salvo ainda). */
  async function readStored(): Promise<StoredSettings | null> {
    try {
      // 15 s por tentativa e uma nova tentativa em 502/504/rede (o servidor reinicia no deploy).
      const r = await getWithRetry(URL_AJUSTES);
      if (!r.ok) return null;
      const j = (await r.json()) as {
        settings?: StoredSettings | null;
        pixelId?: unknown;
        measured?: Record<string, unknown>;
      };
      if (typeof j.pixelId === 'string' && j.pixelId.trim()) pixelId = j.pixelId.trim();
      measured = measuredOf(j.measured);
      return j.settings ?? {};
    } catch {
      return null;
    }
  }

  /** Contas da Meta, teto do dono e Economia salva por cima dos ajustes em uso. */
  function overlay(s: Settings, cap: number = state.ownerCap()): Settings {
    const meta = state.realMeta();
    const econ = state.economics();
    return {
      ...s,
      ...(meta ? { meta } : {}),
      ...(s.goals ? { goals: { ...s.goals, dailySpendCap: cap } } : {}),
      ...(s.economicsV2 || Object.keys(econ).length
        ? { economicsV2: { ...(s.economicsV2 ?? {}), ...measured, ...econ } as EconomicsV2 }
        : {}),
    };
  }

  /**
   * A abertura não leu o salvo e agora deu: o salvo entra no estado e na simulação (com a mudança de
   * agora por cima), para a tela e a próxima gravação partirem do que está salvo, não dos padrões.
   */
  function adopt(
    old: ReturnType<typeof splitStored>,
    settingsPatch: Partial<Settings>,
    economics: Partial<EconomicsV2>,
  ): void {
    state.setEconomics({ ...old.economics, ...state.economics() });
    state.setExtras({ ...old.extras, ...state.extras() });
    state.setOwnerCap(savedCapOf(old.settings));
    const prev = state.settings();
    if (prev) state.apply(overlay(mergeSections(prev, ownerOnly(old.settings))));
    const simPatch = forSim(mergeSections(ownerOnly(old.settings) as Settings, settingsPatch));
    provider.updateSettings(simPatch as Parameters<DataProvider['updateSettings']>[0]).catch(() => undefined);
    if (v2.updateEconomics && Object.keys(economics).length)
      v2.updateEconomics(economics).catch(() => undefined);
    state.setLoaded(true);
  }

  /**
   * Grava no servidor os ajustes em uso, a Economia e os registros à parte (tudo junto: o servidor
   * guarda o salvo inteiro). Se a abertura não conseguiu ler o salvo, lê antes: gravar sem ler
   * apagaria as seções, a Economia e os registros guardados. Devolve as seções gravadas (com o salvo
   * lido agora, quando foi o caso): é isso que entra no estado.
   */
  async function persist(
    settings: Settings,
    economics: Partial<EconomicsV2>,
    extras: Record<string, unknown>,
    patch: Record<string, unknown>,
    settingsPatch: Partial<Settings> = {},
  ): Promise<Settings> {
    let sections = settings;
    let econ = economics;
    let ext = extras;
    if (!state.loaded()) {
      const saved = await readStored();
      if (!saved)
        throw Object.assign(new Error('Não deu para ler os ajustes salvos no servidor. Tente de novo.'), {
          code: 'unavailable',
        });
      const old = splitStored(saved);
      // As seções salvas valem sobre os padrões; a mudança de agora, sobre as salvas.
      sections = mergeSections(mergeSections(settings, ownerOnly(old.settings)), settingsPatch);
      econ = { ...old.economics, ...economics };
      ext = { ...old.extras, ...extras };
      // O que já estava salvo entra no estado (sem a mudança nova: ela só entra se a gravação der certo).
      adopt(old, settingsPatch, econ);
      // O teto que vale: o da mudança de agora, ou o salvo.
      const cap = (settingsPatch.goals as Partial<Settings['goals']> | undefined)?.dailySpendCap;
      sections = overlay(sections, typeof cap === 'number' ? Math.max(0, cap) : state.ownerCap());
    }
    const r = await fetch(URL_AJUSTES, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ settings: toStored(sections, econ, ext), patch }),
    });
    if (!r.ok)
      throw Object.assign(new Error('Não deu para salvar os ajustes no servidor.'), { code: 'forbidden' });
    return sections;
  }

  /**
   * Aplica os ajustes salvos por cima dos padrões, na abertura (a simulação guarda só na memória). O
   * que o dono nunca cadastrou não vem da simulação: sem despesas salvas, nenhuma despesa (antes
   * apareciam as estimativas do estudo como se fossem cadastradas); sem imposto sobre a receita salvo,
   * 0% (a DRE ao vivo fecha no resultado da empresa, sem imposto, até o dono cadastrar a taxa).
   */
  async function withSavedSettings<B extends { settings: Settings }>(boot: B): Promise<B> {
    const saved = await readStored();
    state.setLoaded(saved != null);
    const parts = splitStored(saved);
    state.setEconomics(parts.economics);
    state.setExtras(parts.extras);
    const patch = ownerOnly(parts.settings);
    state.setOwnerCap(savedCapOf(parts.settings));
    let out = boot;
    try {
      const settings = await provider.updateSettings(
        forSim(patch) as Parameters<DataProvider['updateSettings']>[0],
      );
      out = { ...boot, settings };
    } catch {
      // Sem permissão de gravar na simulação: aplica só aqui (as contas reais leem estes ajustes).
      out = { ...boot, settings: mergeSections(boot.settings, patch) };
    }
    // Economia salva também na simulação (onde ela confere permissão e faixa); sem permissão, só aqui.
    if (v2.updateEconomics && Object.keys(parts.economics).length)
      await v2.updateEconomics(parts.economics).catch(() => undefined);
    // Contas da Meta: as reais do boot (o módulo de mídia já trocou as simuladas); pixel do servidor.
    state.setRealMeta(realMetaOf((out as { accounts?: AdAccount[] }).accounts, pixelId));
    out = { ...out, settings: overlay(out.settings) };
    const viewer = (out as { viewer?: Viewer }).viewer;
    if (viewer) out = { ...out, viewer: withoutSimControl(viewer) };
    state.apply(out.settings);
    return out;
  }

  async function updateSettings(patch: Parameters<DataProvider['updateSettings']>[0]): Promise<Settings> {
    const p = patch as Partial<Settings>;
    const capIn = (p.goals as Partial<Settings['goals']> | undefined)?.dailySpendCap;
    const cap = typeof capIn === 'number' ? Math.max(0, capIn) : state.ownerCap();
    const settings = overlay(
      await provider.updateSettings(forSim(p) as Parameters<DataProvider['updateSettings']>[0]),
      cap,
    );
    // O que foi gravado de fato (com o salvo lido agora, se a abertura não tinha lido) entra no estado.
    const saved = await persist(
      settings,
      state.economics(),
      state.extras(),
      patch as Record<string, unknown>,
      p,
    );
    if (typeof capIn === 'number') state.setOwnerCap(cap);
    const out = overlay(saved);
    state.apply(out);
    return out;
  }

  /**
   * Economia e VSL: a simulação confere a permissão e as faixas; o que o dono mudou vai para o
   * servidor (sem gravar, erro: a tela não diz "Salvo") e volta na próxima abertura.
   */
  async function updateEconomics(patch: Partial<EconomicsV2>): Promise<EconomicsV2> {
    const fromSim = v2.updateEconomics ? await v2.updateEconomics(patch) : null;
    const cur = current();
    const econ = { ...state.economics(), ...patch };
    const saved = await persist(cur, econ, state.extras(), { economicsV2: Object.keys(patch) });
    state.setEconomics({ ...state.economics(), ...patch });
    state.apply(overlay(saved));
    return { ...(fromSim ?? saved.economicsV2 ?? {}), ...measured, ...state.economics() } as EconomicsV2;
  }

  /** Ajustes em uso; antes da abertura não há o que gravar (gravar `{}` apagaria o salvo). */
  function current(): Settings {
    const cur = state.settings();
    if (!cur)
      throw Object.assign(new Error('Os ajustes ainda estão carregando. Tente de novo.'), {
        code: 'unavailable',
      });
    return cur;
  }

  extrasByCtx.set(ctx, {
    get: <T>(key: string) => state.extras()[key] as T | undefined,
    async save(key, value) {
      const cur = current();
      const ext = { ...state.extras(), [key]: value };
      const saved = await persist(cur, state.economics(), ext, { painelExtras: [key] });
      state.setExtras({ ...state.extras(), [key]: value });
      state.apply(overlay(saved));
    },
  });

  return {
    overrides: { updateSettings, ...(v2.updateEconomics ? { updateEconomics } : {}) },
    patchBoot: withSavedSettings,
  };
}
